<script lang="ts" setup>

import { config } from '@layouts/config'
import { injectionKeyIsVerticalNavHovered } from '@layouts/symbols'
import { isNavItemVisible } from '@/composables/useNavItemVisibility';
import { navItem } from '@/navigation/vertical';
import { useNavGridLayout } from '@/composables/useNavGridLayout';

const appEnv = import.meta.env.VITE_APP_ENVIRONMENT;
interface Props {
  tag?: string|null,
  navItems: navItem[],
  pinnedNavItems?: navItem[],
  isOverlayNavActive: boolean,
  toggleIsOverlayNavActive: (active: boolean) => void,
};

const props = withDefaults(defineProps<Props>(), {
  tag: 'aside',
  pinnedNavItems: () => [],
});

// DEVAMS-2663: Search/quick-filter state for nav items
const searchQuery = ref('')

const refNav = ref();
const { width: windowWidth } = useWindowSize();
const isHovered = useElementHover(refNav);

// DEVAMS-2670: adaptive grid tier (phone/tabletPortrait/wide) drives column
// count and overlay drawer width. The full nav is ALWAYS an overlay now (see
// VerticalNavLayout.vue) — the separate always-visible favorites rail lives
// outside this component entirely.
const navGridLayout = useNavGridLayout(windowWidth);

provide(injectionKeyIsVerticalNavHovered, isHovered)

const route = useRoute()

watch(() => route.name, () => {
  props.toggleIsOverlayNavActive(false)
})

const isVerticalNavScrolled = ref(false)
const updateIsVerticalNavScrolled = val => isVerticalNavScrolled.value = val

// DEVAMS-2670: the full nav overlay always shows every item (favorites are
// no longer excluded here) — the separate persistent rail is just a
// shortcut, not the only place favorites are reachable.
const combinedNavItems = computed<navItem[]>(() => [...props.pinnedNavItems, ...props.navItems])

// DEVAMS-2663: Filter nav items based on search query
const filteredNavItems = computed(() => {
  if (!searchQuery.value) return combinedNavItems.value

  const query = searchQuery.value.toLowerCase()
  return combinedNavItems.value.filter(item => {
    if ('heading' in item) return false // Skip section titles
    const label = item.title?.toLowerCase() || ''
    return label.includes(query)
  })
})
</script>

<template>
  <Component
    :is="props.tag"
    ref="refNav"
    class="layout-vertical-nav overlay-nav"
    :class="[
      {
        'hovered': isHovered,
        'visible': isOverlayNavActive,
        'scrolled': isVerticalNavScrolled,
      },
      `nav-grid-tier-${navGridLayout.tier}`,
    ]"
    :style="{ inlineSize: navGridLayout.overlayWidth }"
  >
    <!-- 👉 Header -->
    <!-- DEVAMS-2670: the app logo AND its "Platcop" title text now live
         together, permanently, in the top navbar
         (DefaultLayoutWithVerticalNav.vue) — not here. Second-round review
         fixed a bug where only the logo icon had moved out, leaving the
         title text span orphaned in this drawer; nothing brand-related
         remains here now, just the drawer's own close action. -->
    <div class="nav-header">
      <slot name="nav-header">
        <!-- 👉 Vertical nav actions -->
        <Component
          :is="config.app.iconRenderer || 'div'"
          class="header-action"
          v-bind="config.icons.close"
          @click="toggleIsOverlayNavActive(false)"
        />
      </slot>
    </div>
    <slot name="before-nav-items">
      <div
        v-if="appEnv !== 'production'"
        class="text-center"
      >
        env: {{ appEnv }}
      </div>
      <div class="vertical-nav-items-shadow" />
    </slot>

    <!-- DEVAMS-2670: search input filters the full 19-item grid. Favorites
         now live in the separate always-visible NavFavoritesRail. -->
    <div class="nav-search-favorites-section">
      <VTextField
        v-model="searchQuery"
        density="compact"
        placeholder="Search nav..."
        clearable
        hide-details
        class="nav-search-input"
      >
        <template #prepend-inner>
          <VIcon icon="mdi-magnify" />
        </template>
      </VTextField>
    </div>

    <slot
      name="nav-items"
      :update-is-vertical-nav-scrolled="updateIsVerticalNavScrolled"
    >
      <!-- DEVAMS-2670: adaptive grid replaces the single-column nav list on
           every breakpoint — column count comes from navGridLayout. -->
      <div
        class="nav-items-grid"
        :style="{ '--nav-grid-cols': navGridLayout.columns }"
      >
        <template v-for="(item, index) in filteredNavItems">
          <RouterLink
            v-if="!('heading' in item) && isNavItemVisible(item)"
            :key="'vertical_nav' + index"
            :to="item.to"
            class="nav-grid-tile"
            :title="item.title"
            :data-nav-route="item.to.name"
          >
            <span
              v-if="item.badgeContent"
              class="nav-grid-tile-badge"
              :class="item.badgeClass"
            >
              {{ item.badgeContent }}
            </span>
            <Component
              :is="config.app.iconRenderer || 'div'"
              v-if="item.icon"
              v-bind="item.icon"
              class="nav-grid-tile-icon"
            />
            <span class="nav-grid-tile-label">{{ item.title }}</span>
          </RouterLink>
        </template>
      </div>
    </slot>
  </Component>
</template>

<style lang="scss">
@use "@configured-variables" as variables;
@use "@layouts/styles/mixins";

// 👉 Vertical Nav
.layout-vertical-nav {
  position: fixed;
  z-index: variables.$layout-vertical-nav-z-index;
  display: flex;
  flex-direction: column;
  block-size: 100%;
  inline-size: variables.$layout-vertical-nav-width;
  inset-block-start: 0;
  inset-inline-start: 0;
  transition: transform 0.25s ease-in-out, inline-size 0.25s ease-in-out, box-shadow 0.25s ease-in-out;
  will-change: transform, inline-size;

  .nav-header {
    display: flex;
    align-items: center;
    // DEVAMS-2670 second-round review: only the close action remains here
    // now that the brand lockup (logo + title) moved fully into the top
    // navbar — push it to the end instead of space-between (which needed a
    // second flex item to make sense).
    justify-content: flex-end;
    padding-block-start: var(--safe-area-inset-top, 0px);

    .header-action {
      cursor: pointer;
    }
  }

  .nav-items {
    block-size: 100%;

    // ℹ️ We no loner needs this overflow styles as perfect scrollbar applies it
    overflow-x: hidden;

    // // ℹ️ We used `overflow-y` instead of `overflow` to mitigate overflow x. Revert back if any issue found.
    overflow-y: auto;
  }

  .nav-item-title {
    overflow: hidden;
    margin-inline-end: auto;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  // DEVAMS-2670: search section only now — the favorites row moved out to
  // the separate always-visible NavFavoritesRail component.
  .nav-search-favorites-section {
    padding: 0.75rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
    flex-shrink: 0;
  }

  .nav-search-input {
    :deep(.v-field__input) {
      font-size: 0.875rem;
      padding: 0 0.5rem;
    }
  }

  // 👉 Collapsed
  .layout-vertical-nav-collapsed & {
    &:not(.hovered) {
      inline-size: variables.$layout-vertical-nav-collapsed-width;
    }
  }

  // 👉 Overlay nav
  &.overlay-nav {
    &:not(.visible) {
      // DEVAMS-2670: percentage-based so the hidden offset always matches the
      // tier's own (dynamically overridden) inline-size, not just phone's.
      transform: translateX(-100%);

      @include mixins.rtl {
        transform: translateX(100%);
      }
    }
  }

  // DEVAMS-2670: adaptive nav item grid — column count set per-tier via the
  // --nav-grid-cols custom property (useNavGridLayout). Flat/bordered tiles,
  // no drop-shadow, no rounded-card style — see DEVAMS-2670 AC.
  .nav-items-grid {
    display: grid;
    grid-template-columns: repeat(var(--nav-grid-cols), minmax(0, 1fr));
    // DEVAMS-2670: without this, auto rows stretch to fill any leftover
    // container height on tall/web viewports, inflating every tile.
    align-content: start;
    gap: 0.5rem;
    padding: 0.75rem;
    overflow-x: hidden;
    overflow-y: auto;
    flex: 1 1 auto;
  }

  .nav-grid-tile {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.375rem;
    min-inline-size: 0;
    padding: 0.75rem 0.375rem;
    border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    border-radius: 0.375rem;
    text-decoration: none;
    color: inherit;
    background-color: rgb(var(--v-theme-surface));
    transition: background-color 0.2s ease, border-color 0.2s ease;

    &:hover {
      border-color: rgb(var(--v-theme-primary));
      background-color: rgba(var(--v-theme-primary), 0.04);
    }

    &.router-link-active {
      border-color: rgb(var(--v-theme-primary));
      color: rgb(var(--v-theme-primary));
    }
  }

  .nav-grid-tile-icon {
    font-size: 1.5rem;
  }

  .nav-grid-tile-label {
    inline-size: 100%;
    overflow: hidden;
    font-size: 0.75rem;
    line-height: 1.15;
    text-align: center;
    text-overflow: ellipsis;
    overflow-wrap: break-word;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  .nav-grid-tile-badge {
    position: absolute;
    inset-block-start: 0.25rem;
    inset-inline-end: 0.25rem;
    min-inline-size: 1.125rem;
    padding: 0 0.25rem;
    border-radius: 999px;
    font-size: 0.625rem;
    line-height: 1.125rem;
    text-align: center;
    color: rgb(var(--v-theme-on-error));
    background-color: rgb(var(--v-theme-error));
  }

  // DEVAMS-2670: phone's overlay is wider (88vw, see useNavGridLayout) but
  // its tiles still get slightly tighter padding than tablet/web so the
  // 4-col grid doesn't feel cramped on a ~390px viewport.
  &.nav-grid-tier-phone .nav-grid-tile {
    padding: 0.5rem 0.25rem;
  }
}
</style>
